import { t } from '@lingui/core/macro';
import { useEffect, useMemo } from 'react';

import { type OptionType } from '@/api/common';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type SelectProps,
} from '@/components/Form/pickers/primitives/select';
import SelectEmpty from '@/components/Form/pickers/primitives/select/SelectEmpty';
import SelectStatus from '@/components/Form/pickers/primitives/select/SelectStatus';

type Props = SelectProps<OptionType, false> & {
  'aria-invalid'?: boolean;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  mode?: unknown;
  allowClear?: boolean;
  value?: OptionType;
  onChange?: (value: OptionType | undefined) => void;
};

const IntentProviderSelect = ({
  value,
  onChange,
  disabled = false,
  size = 'large',
  className,
  'aria-invalid': ariaInvalid,
  placeholder = t`Select Intent Provider...`,
  mode: _mode,
  allowClear: _allowClear,
  ...rest
}: Props) => {
  void _mode;
  void _allowClear;
  const isLoading = false;

  const intentProviders = useMemo<OptionType[]>(() => [{ id: 'oprv:g2', name: 'G2' }], []);
  const resolvedValue = useMemo(
    () => intentProviders.find((provider) => provider.id === value?.id),
    [intentProviders, value?.id],
  );

  // Auto-select first option if no value is set
  useEffect(() => {
    if (!resolvedValue && intentProviders.length > 0 && onChange && !isLoading) {
      onChange(intentProviders[0]);
    }
  }, [resolvedValue, intentProviders, onChange, isLoading]);

  return (
    <Select<OptionType>
      isLoading={isLoading}
      disabled={disabled}
      size={size}
      value={resolvedValue ?? null}
      onValueChange={(option) => onChange?.(option ?? undefined)}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      {...rest}
    >
      <SelectTrigger className={className} aria-invalid={ariaInvalid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectStatus />
        <SelectEmpty items={intentProviders} />
        {intentProviders?.map((provider) => (
          <SelectItem key={provider.id} value={provider}>
            {provider.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default IntentProviderSelect;
